import { useDataTableContext } from '../../components/DataTable/DataTableContext'
import useApiService from '../../services/api'
import lodash from 'lodash'
import { useMemo, useState } from 'react'
import confirm from '../../components/dialogConfirm'
import { CompanyTypeSlug, TableTypeSlug } from '../../models/companies'
import { UnifiedTargetType } from '../../services/opportunities/targetTypeMap'
import { Combobox } from '../../components/UI/combobox/combobox'
import { CreateEditCampaignModal } from './CreateEditCampaignModal'
import { CreateCampaignModalV2 } from './CreateCampaignModalV2'
import { countDistinctCompanies } from './campaignPushTarget'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useDropdownSuppression } from '../../components/UI/Dropdown/Dropdown'

export interface AssignToCampaignModalProps {
  accessorKey: string
  tableQueryKey: string[]
  requestParams: Record<string, any>
  tableTypeSlug: TableTypeSlug
  companyTypeSlug?: CompanyTypeSlug
  data: any[]
  buttonLess?: boolean
  /** True when rendered on a doors table. Enables the "N doors resolve to M
   *  companies" summary line, since several doors of the same company add that
   *  company once (FIR-3963). */
  isDoorsTable?: boolean
  /** Accessor for a door row's parent chain-proxy id (e.g. "chain_proxy.id"),
   *  used to dedupe the loaded selection by company for the summary line. */
  chainProxyIdAccessorKey?: string
  /** When set, route the assign through the unified `opportunities/[doors/]campaigns/assign/`
   *  endpoint for this target type so a select-all re-resolves the SAME set the
   *  unified list rendered (FIR-7822). Omitted = legacy per-vertical endpoint. */
  unifiedTargetType?: UnifiedTargetType
  /** Use the doors-scoped unified endpoint variant (locations tables). */
  unifiedDoors?: boolean
}

export function AssignToCampaignModal(props: AssignToCampaignModalProps) {
  const api = useApiService()
  const featureFlags = useFeatureFlagService()
  const isMultiType = !!featureFlags.enableEmailSequencing
  const { data: campaigns, isLoading } = api.useGetCampaigns()

  const mutation = api.useAssignToCampaignMutation(
    props.tableQueryKey,
    props.accessorKey,
    props.tableTypeSlug,
    props.unifiedTargetType,
    props.unifiedDoors
  )
  const {
    state: { rowSelection, isAllRowsSelected, totalSelectedRows },
  } = useDataTableContext()

  const [openNewCampaign, setOpenNewCampaign] = useState(false)
  // When this picker is rendered inside a bulk-actions dropdown sub-menu,
  // opening the create-campaign modal must hide that dropdown so it does
  // not linger on top of the modal (FIR-7473). We suppress (not close)
  // the menu so this component stays mounted and its post-create assign
  // logic survives. Resolves to a no-op outside a Dropdown.
  const dropdown = useDropdownSuppression()

  function handleOpenNewCampaign() {
    dropdown.suppress()
    setOpenNewCampaign(true)
  }

  function handleCloseNewCampaign() {
    setOpenNewCampaign(false)
    dropdown.restore()
  }

  const campaignOptions = useMemo(
    () =>
      campaigns?.results.map((c) => ({
        label: c.name,
        value: c.id,
        icon: (
          <div
            className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
            style={{ backgroundColor: c.color }}
          />
        ),
      })) ?? [],
    [campaigns]
  )

  const selectedRows = props.data?.filter((_, i) => {
    return Boolean(rowSelection?.[i])
  })

  const unselectedRows = props.data?.filter((_, i) => {
    return !rowSelection?.[i]
  })

  const selectedChainProxyIds = selectedRows?.map((r: any) =>
    lodash.get(r, props.accessorKey)
  ) as number[]

  const unselectedChainProxyIds = unselectedRows?.map((r: any) =>
    lodash.get(r, props.accessorKey)
  ) as number[]

  // On a doors table, surface how many distinct companies the selected doors
  // resolve to: several doors of the same company add that company once. We can
  // only compute this reliably when every selected door row is loaded
  // client-side (an explicit, single-page selection); deduping by parent chain
  // proxy is then always <= the door count. For select-all the rows are not all
  // loaded and the doors summary counts are unreliable, so we omit the number
  // and rely on the explanatory note below (FIR-3963).
  const doorCount = totalSelectedRows
  const selectionFullyLoaded =
    !isAllRowsSelected && (selectedRows?.length ?? 0) === totalSelectedRows
  const relatedCompanyCount: number | null =
    props.isDoorsTable && selectionFullyLoaded && props.chainProxyIdAccessorKey
      ? countDistinctCompanies(
          selectedRows ?? [],
          props.chainProxyIdAccessorKey
        )
      : null

  async function handleSubmit(
    campaignId: number | string,
    campaignName?: string,
    campaignColor?: string,
    campaignTypeSlug?: string
  ) {
    const selected = campaigns?.results.find((it) => it.id == campaignId)
    const targetTypeSlug = props.companyTypeSlug ?? ''
    // Use explicitly passed type (for newly created campaigns not yet in cache)
    // or fall back to cached campaign data
    const existingTypeSlug = campaignTypeSlug ?? selected?.type?.slug

    // Detect type mismatch: pushing type X targets to a type Y campaign
    const hasMismatch =
      !!existingTypeSlug &&
      !!targetTypeSlug &&
      existingTypeSlug !== targetTypeSlug

    // Hide the bulk-actions dropdown for the duration of the confirm
    // dialog / assignment so it does not linger on top of them (FIR-7473).
    // No-op outside a Dropdown.
    dropdown.suppress()
    try {
      if (hasMismatch) {
        const typeName = selected?.type?.name ?? existingTypeSlug
        const confirmed = await confirm(
          `Adding these targets to a ${typeName} campaign will clear the opportunity type, which limits the columns visible in your campaign targets table. Continue?`,
          'Opportunity Type Mismatch'
        )
        if (!confirmed) return
      } else {
        const confirmed = await confirm(
          `Confirm assigning ${totalSelectedRows} items to campaign?`,
          'Assign to Campaign'
        )
        if (!confirmed) return
      }

      await mutation.mutateAsync({
        campaign_name: campaignName ?? selected?.name ?? '',
        campaign_color: campaignColor ?? selected?.color ?? '',
        campaign_id: parseInt(campaignId.toString()),
        include_ids: isAllRowsSelected ? [] : selectedChainProxyIds,
        exclude_ids: unselectedChainProxyIds,
        filter_params: props.requestParams,
        target_type_slug: targetTypeSlug,
        clear_type: hasMismatch,
      })
    } finally {
      dropdown.restore()
    }
  }

  return (
    <>
      {!openNewCampaign && (
        <>
          {props.isDoorsTable && doorCount > 0 && (
            <div className="px-3 pt-2 pb-1.5 border-b border-line">
              <p className="text-xs font-medium text-fg">
                {relatedCompanyCount != null
                  ? `Associate ${relatedCompanyCount} ${
                      relatedCompanyCount === 1 ? 'company' : 'companies'
                    } related to ${doorCount} selected ${
                      doorCount === 1 ? 'door' : 'doors'
                    }`
                  : // Select-all (or a selection that spans pages): not every
                    // door row is loaded, and the doors-table selected-count is
                    // unreliable here, so we assert no specific numbers.
                    'Associate the companies related to your selected doors'}
              </p>
              <p className="mt-0.5 text-xxs text-fg-tertiary">
                Multiple doors from the same company add that company once.
              </p>
            </div>
          )}
          <Combobox
            options={campaignOptions}
            searchPlaceholder="Search Campaigns..."
            placeholder="Push to Campaign"
            onSelectAltAction={handleSubmit}
            onAddNew={handleOpenNewCampaign}
            loading={isLoading || mutation.isPending}
            buttonLess={props.buttonLess}
          />
        </>
      )}
      {isMultiType || !props.companyTypeSlug ? (
        <CreateCampaignModalV2
          key={props.companyTypeSlug ?? 'none'}
          show={openNewCampaign}
          onHide={handleCloseNewCampaign}
          defaultTypeSlug={props.companyTypeSlug}
          onCreated={async (campaign) => {
            await handleSubmit(
              campaign.id,
              campaign.name,
              campaign.color,
              campaign.typeSlug
            )
          }}
        />
      ) : (
        <CreateEditCampaignModal
          isOpen={openNewCampaign}
          onClose={handleCloseNewCampaign}
          defaultCampaignCompanyType={props.companyTypeSlug}
          pushToCampaignOptions={{
            data: props.data,
            accessorKey: props.accessorKey,
            requestParams: props.requestParams,
            tableQueryKey: props.tableQueryKey,
            totalSelectedRows: totalSelectedRows,
          }}
        />
      )}
    </>
  )
}
